import React from 'react'
import AddPayment from './AddPayment';
import { confirmAlert } from 'react-confirm-alert';
import { withAllContexts } from '../../../AllContextsWrapper';
import { enqueueSnackbar } from 'notistack';
import { Euro, Trash2, Wallet, AlertCircle } from 'lucide-react';

const Payments = ({ reservation, app, socketIO }) => {

  const deletePaymentConfirmation = (paymentId) => {
    confirmAlert({
      title: 'Zmazanie platby',
      message: 'Naozaj chceš vymazať túto platbu?',
      buttons: [
        {
          label: 'Zrušiť'
        },
        {
          label: 'Zmazať',
          onClick: () => deletePayment(paymentId)
        }
      ]
    });
  }

  const deletePayment = (paymentId) => {
    socketIO.socket.emit('payment:delete', paymentId, (response) => {
      if (response.success === true) {
        app.functions.updateCurrReservation(reservation.id);
        app.functions.updateMainCalendar();
        enqueueSnackbar("Platba úspešne odstránená", { variant: "success" });
      } else {
        enqueueSnackbar("Platbu sa nepodarilo odstrániť", { variant: "error" });
      }
    });
  }

  const payments = reservation.Payments || [];
  const paymentTypes = app.functions.getPaymentTypes();

  const isCamping = app.state.activeCalendar?.type === 'accommodation';
  const primaryTextClass = isCamping ? "text-taborisko-pine" : "text-splavma-teal";
  const rowHoverClass = isCamping ? "hover:bg-taborisko-pine/5" : "hover:bg-splavma-teal/5";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-success-100 rounded-xl text-success-600">
          <Euro className="w-5 h-5" />
        </div>
        <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide">História platieb</h2>
      </div>

      {payments.length > 0 ? (
        <div className="space-y-4 mb-8">
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                    <th className="px-6 py-4">Spôsob úhrady</th>
                    <th className="px-6 py-4">Dátum</th>
                    <th className="px-6 py-4 text-right">Suma</th>
                    <th className="px-6 py-4 text-center">Prijal</th>
                    <th className="px-6 py-4 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {payments.map((payment, key) => {
                    const typeLabel = payment.type && paymentTypes[payment.type] ? paymentTypes[payment.type] : "----";
                    const dateLabel = new Date(payment.createdAt).toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                    
                    return (
                      <tr key={key + "_payment"} className={`group ${rowHoverClass} transition-colors`}>
                        <td className="px-6 py-4 text-sm font-bold text-gray-900">
                          <span className={`inline-flex items-center px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${isCamping ? 'bg-taborisko-pine/10 text-taborisko-pine' : 'bg-splavma-teal/10 text-splavma-teal'}`}>
                            {typeLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-500">
                          {dateLabel}
                        </td>
                        <td className="px-6 py-4 text-sm font-black text-success-600 text-right">
                          {parseFloat(payment.amount).toFixed(2)} €
                        </td>
                        <td className="px-6 py-4 text-xs font-bold text-gray-500 text-center uppercase tracking-wider">
                          <span className="bg-gray-100 px-2 py-1 rounded-md">
                            {socketIO.usersShort[payment.received_by] || '?'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => deletePaymentConfirmation(payment.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50/80 border-t border-gray-100">
                  <tr>
                    <td colSpan="2" className="px-6 py-5 text-sm font-bold text-gray-900 uppercase tracking-widest text-right">
                      Celkom uhradené:
                    </td>
                    <td className="px-6 py-5 text-lg font-black text-success-600 text-right">
                      {payments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0).toFixed(2)} €
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-2">
            {payments.map((payment, key) => {
              const typeLabel = payment.type && paymentTypes[payment.type] ? paymentTypes[payment.type] : "----";
              const dateLabel = new Date(payment.createdAt).toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

              return (
                <div key={key + "_payment_mobile"} className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm relative group">
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex-1 min-w-0 pr-8">
                      <span className="text-[9px] font-black uppercase tracking-widest text-gray-400 block">
                        Spôsob úhrady
                      </span>
                      <span className={`inline-flex items-center py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider ${isCamping ? 'text-taborisko-pine' : 'text-splavma-teal'}`}>
                        {typeLabel}
                      </span>
                    </div>
                    <button
                      onClick={() => deletePaymentConfirmation(payment.id)}
                      className="p-1.5 text-red-500 bg-red-50 rounded-lg flex-shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50">
                    <div className="text-[10px] font-medium text-gray-500">
                      {dateLabel}
                    </div>
                    <div className="text-base font-black text-success-600">
                      {parseFloat(payment.amount).toFixed(2)}€
                    </div>
                  </div>
                  
                  <div className="absolute top-3 right-10 text-[8px] font-bold text-gray-300 uppercase bg-gray-50/50 px-1 py-0.5 rounded">
                    {socketIO.usersShort[payment.received_by] || '?'}
                  </div>
                </div>
              );
            })}
            
            {/* Mobile Total */}
            <div className="bg-success-600 rounded-xl p-3 flex justify-between items-center text-white shadow-md">
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-80">Celkovo uhradené</span>
              <span className="text-lg font-black">
                {payments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0).toFixed(2)}€
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center mb-8 shadow-sm">
          <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Wallet className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="text-gray-900 font-bold mb-1 uppercase tracking-wide">Žiadne platby</h3>
          <p className="text-gray-500 text-sm font-medium">Pre túto rezerváciu zatiaľ neboli zaevidované žiadne platby.</p>
        </div>
      )}

      <AddPayment reservation={reservation} />
    </div>
  );
}

export default withAllContexts(Payments);
